/**
 * xlsx.js — write a real .xlsx in the browser, with no dependency.
 *
 * Lifted from the pure half of cpwealth_stock_dashboard.py's inlined report
 * script, and byte-for-byte verified against it: the native grid must hand Excel
 * the SAME bytes the HTML report did, or the port silently changes what
 * customers download. That code is covered by
 * tools/cpwealth_dashboard/test_xlsx_export.py, which runs it in Node and opens
 * the result with openpyxl.
 *
 * A genuine workbook rather than a CSV with the wrong extension, so numbers stay
 * numeric and percentages carry a percent format instead of Excel being handed
 * 0.0645 and hoping.
 *
 * Entries are STORED (uncompressed): still a valid zip, and it avoids needing
 * CompressionStream to save bytes on a file the user opens once.
 *
 * buildXlsx is PURE — no DOM, no globals — which is what lets it be tested
 * against openpyxl rather than eyeballed.
 */

const XLSX_FMT = { num: 1, pct: 2, int: 3, txt: 0, hid: 0 };   // -> cellXfs index

function _crcTable(){
  const t = new Uint32Array(256);
  for(let n=0;n<256;n++){ let c=n; for(let k=0;k<8;k++) c = c&1 ? 0xEDB88320^(c>>>1) : c>>>1; t[n]=c>>>0; }
  return t;
}
const CRC_T = _crcTable();
function crc32(bytes){
  let c = 0xFFFFFFFF;
  for(let i=0;i<bytes.length;i++) c = CRC_T[(c ^ bytes[i]) & 0xFF] ^ (c>>>8);
  return (c ^ 0xFFFFFFFF)>>>0;
}
function _colRef(i){                     // 0 -> A, 25 -> Z, 26 -> AA
  let s = "";
  for(i = i + 1; i > 0; i = Math.floor((i - 1) / 26)) s = String.fromCharCode(65 + (i - 1) % 26) + s;
  return s;
}
function _x(s){                          // XML-escape, incl. quotes/control chars
  return String(s).replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&apos;"}[c]))
                  // Control characters are the point: XML 1.0 forbids them, so a
                  // stray one from BigQuery would make the workbook unopenable.
                  // Kept byte-identical to the report's copy — do not "fix".
                  // eslint-disable-next-line no-control-regex
                  .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, "");
}

/** cols: [[header, fmt], …] (fmt 'hid' skipped) · rows: aligned value arrays. */
export function buildXlsx(cols, rows, sheetName){
  const show = cols.map((c,i)=>i).filter(i => cols[i][1] !== "hid");

  let sheet = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
    + '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>';
  // Header row, bold via xf 4.
  sheet += '<row r="1">' + show.map((ci,n) =>
    `<c r="${_colRef(n)}1" s="4" t="inlineStr"><is><t>${_x(cols[ci][0])}</t></is></c>`).join("") + '</row>';
  rows.forEach((r, ri) => {
    const cells = show.map((ci, n) => {
      const ref = _colRef(n) + (ri + 2);
      const v = r[ci], fmt = cols[ci][1], s = XLSX_FMT[fmt] ?? 0;
      if(v === null || v === undefined || v === "") return "";        // leave blank, as the grid does
      if(fmt === "txt") return `<c r="${ref}" t="inlineStr"><is><t>${_x(v)}</t></is></c>`;
      // Numeric: write the RAW value and let the number format render it, so the
      // cell stays computable (pct stays a fraction -> Excel shows 6.45%).
      return `<c r="${ref}" s="${s}"><v>${v}</v></c>`;
    }).join("");
    sheet += `<row r="${ri + 2}">${cells}</row>`;
  });
  sheet += '</sheetData></worksheet>';

  const styles = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
    + '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
    + '<numFmts count="3">'
    + '<numFmt numFmtId="164" formatCode="#,##0.00"/>'
    + '<numFmt numFmtId="165" formatCode="0.00%"/>'
    + '<numFmt numFmtId="166" formatCode="0"/>'
    + '</numFmts>'
    + '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>'
    + '<font><b/><sz val="11"/><name val="Calibri"/></font></fonts>'
    + '<fills count="1"><fill><patternFill patternType="none"/></fill></fills>'
    + '<borders count="1"><border/></borders>'
    + '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>'
    + '<cellXfs count="5">'
    + '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>'                        // 0 general
    + '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' // 1 num
    + '<xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' // 2 pct
    + '<xf numFmtId="166" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' // 3 int
    + '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>'           // 4 header
    + '</cellXfs>'
    // Without a Normal cellStyle, readers warn ("workbook contains no default
    // style") and have to invent one. Cheap to declare; don't rely on tolerance.
    + '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>'
    + '</styleSheet>';

  const parts = [
    ["[Content_Types].xml",
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
      + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
      + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
      + '<Default Extension="xml" ContentType="application/xml"/>'
      + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
      + '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>'
      + '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
      + '</Types>'],
    ["_rels/.rels",
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
      + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
      + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>'
      + '</Relationships>'],
    ["xl/workbook.xml",
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
      + '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"'
      + ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
      + `<sheets><sheet name="${_x(sheetName).slice(0,31)}" sheetId="1" r:id="rId1"/></sheets></workbook>`],
    ["xl/_rels/workbook.xml.rels",
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
      + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
      + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>'
      + '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>'
      + '</Relationships>'],
    ["xl/styles.xml", styles],
    ["xl/worksheets/sheet1.xml", sheet],
  ];

  // ── minimal zip (stored entries) ──
  const enc = new TextEncoder();
  const locals = [], central = [];
  let offset = 0;
  for(const [name, text] of parts){
    const nameB = enc.encode(name), data = enc.encode(text), crc = crc32(data);
    const lh = new DataView(new ArrayBuffer(30));
    lh.setUint32(0, 0x04034b50, true); lh.setUint16(4, 20, true); lh.setUint16(6, 0, true);
    lh.setUint16(8, 0, true);                       // stored
    lh.setUint16(10, 0, true); lh.setUint16(12, 0, true);   // no timestamp: keeps output deterministic
    lh.setUint32(14, crc, true);
    lh.setUint32(18, data.length, true); lh.setUint32(22, data.length, true);
    lh.setUint16(26, nameB.length, true); lh.setUint16(28, 0, true);
    locals.push(new Uint8Array(lh.buffer), nameB, data);

    const cd = new DataView(new ArrayBuffer(46));
    cd.setUint32(0, 0x02014b50, true); cd.setUint16(4, 20, true); cd.setUint16(6, 20, true);
    cd.setUint16(8, 0, true); cd.setUint16(10, 0, true);
    cd.setUint16(12, 0, true); cd.setUint16(14, 0, true);
    cd.setUint32(16, crc, true);
    cd.setUint32(20, data.length, true); cd.setUint32(24, data.length, true);
    cd.setUint16(28, nameB.length, true); cd.setUint16(30, 0, true); cd.setUint16(32, 0, true);
    cd.setUint16(34, 0, true); cd.setUint16(36, 0, true); cd.setUint32(38, 0, true);
    cd.setUint32(42, offset, true);
    central.push(new Uint8Array(cd.buffer), nameB);
    offset += 30 + nameB.length + data.length;
  }
  const cdSize = central.reduce((n, a) => n + a.length, 0);
  const eocd = new DataView(new ArrayBuffer(22));
  eocd.setUint32(0, 0x06054b50, true);
  eocd.setUint16(8, parts.length, true); eocd.setUint16(10, parts.length, true);
  eocd.setUint32(12, cdSize, true); eocd.setUint32(16, offset, true);
  eocd.setUint16(20, 0, true);

  const chunks = [...locals, ...central, new Uint8Array(eocd.buffer)];
  const total = chunks.reduce((n, a) => n + a.length, 0);
  const out = new Uint8Array(total);
  let p = 0;
  for(const c of chunks){ out.set(c, p); p += c.length; }
  return out;
}
